<script setup>
import { ref, defineProps, defineEmits, computed } from "vue"
import { useStore } from "vuex"
import { notify } from "notiwind"
import { useI18n } from "vue-i18n"
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
  Switch,
  SwitchGroup,
  SwitchLabel,
} from "@headlessui/vue"

const store = useStore()
const loading = ref(false)

const props = defineProps({
  modelValue: Boolean,
  email: {
    type: String,
    required: false,
    default: "",
  },
})
const emit = defineEmits(["update:modelValue", "confirm"])

const open = computed({
  get() {
    return props.modelValue
  },

  set(value) {
    return emit("update:modelValue", value)
  },
})

const form = ref({ email: props.email })
const tcAccepted = ref(false)
const { t } = useI18n()

async function submit() {
  try {
    loading.value = true

    await store.dispatch("newsletters/subscribeHouseholderNewsletter", {
      params: form.value,
    })

    notify(
      {
        title: t("snackbars.success.title"),
        text: t("snackbars.success.common"),
        type: "info",
        group: "foo",
      },
      4000,
    )
  } catch {
    notify(
      {
        title: t("common.warning"),
        text: t("snackbars.errors.failed"),
        type: "warning",
        group: "foo",
      },
      4000,
    )
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-40" @close="open = false">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-40 overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6"
            >
              <div>
                <div>
                  <img
                    class="mx-auto h-12 w-auto pr-2"
                    src="@assets/images/logo.svg"
                    alt="Your Company"
                  />
                  <h2
                    class="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900"
                  >
                    {{ $t("common.newsletter-dialog.title") }}
                  </h2>
                  <p class="mt-2 text-center text-sm text-gray-600">
                    {{ $t("common.newsletter-dialog.description") }}
                  </p>
                </div>
                <form
                  action="#"
                  method="POST"
                  class="mx-auto mt-10 max-w-xl sm:mt-10"
                >
                  <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div>
                      <div class="flex justify-between">
                        <label
                          for="first-name"
                          class="block text-sm font-medium leading-6 text-gray-900"
                        >
                          {{
                            $t("common.newsletter-dialog.form.first-name")
                          }}</label
                        >
                        <span
                          id="email-optional"
                          class="text-sm leading-6 text-gray-500"
                          >{{ $t("common.optional") }}</span
                        >
                      </div>
                      <div class="mt-2.5">
                        <input
                          id="first-name"
                          v-model="form.firstName"
                          type="text"
                          name="first-name"
                          autocomplete="given-name"
                          class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                    <div>
                      <div class="flex justify-between">
                        <label
                          for="last-name"
                          class="block text-sm font-medium leading-6 text-gray-900"
                          >{{
                            $t("common.newsletter-dialog.form.last-name")
                          }}</label
                        >
                        <span
                          id="email-optional"
                          class="text-sm leading-6 text-gray-500"
                          >{{ $t("common.optional") }}</span
                        >
                      </div>
                      <div class="mt-2.5">
                        <input
                          id="last-name"
                          v-model="form.lastName"
                          type="text"
                          name="last-name"
                          autocomplete="family-name"
                          class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                    <div class="sm:col-span-2">
                      <div class="flex justify-between">
                        <label
                          for="company"
                          class="block text-sm font-medium leading-6 text-gray-900"
                          >{{
                            $t("common.newsletter-dialog.form.company")
                          }}</label
                        >
                        <span
                          id="email-optional"
                          class="text-sm leading-6 text-gray-500"
                          >{{ $t("common.optional") }}</span
                        >
                      </div>
                      <div class="mt-2.5">
                        <input
                          id="company"
                          v-model="form.companyName"
                          type="text"
                          name="company"
                          autocomplete="organization"
                          class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                    <div class="sm:col-span-2">
                      <label
                        for="email"
                        class="block text-sm font-medium leading-6 text-gray-900"
                        >{{ $t("common.newsletter-dialog.form.email") }}</label
                      >
                      <div class="mt-2.5">
                        <input
                          id="email"
                          v-model.trim="form.email"
                          type="email"
                          name="email"
                          autocomplete="email"
                          class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <SwitchGroup as="div" class="flex gap-x-4 sm:col-span-2">
                      <div class="flex h-6 items-center">
                        <Switch
                          v-model="tcAccepted"
                          :class="[
                            tcAccepted ? 'bg-indigo-600' : 'bg-gray-200',
                            'flex w-8 flex-none cursor-pointer rounded-full p-px ring-1 ring-inset ring-gray-900/5 transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
                          ]"
                        >
                          <span class="sr-only">{{
                            $t("common.newsletter-dialog.agree-to-policies")
                          }}</span>
                          <span
                            aria-hidden="true"
                            :class="[
                              tcAccepted ? 'translate-x-3.5' : 'translate-x-0',
                              'h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition duration-200 ease-in-out',
                            ]"
                          />
                        </Switch>
                      </div>
                      <SwitchLabel class="text-sm leading-6 text-gray-600">
                        {{ $t("common.newsletter-dialog.switch-agree") }}
                        {{ " " }}
                        <a
                          href="/help/guides/installers/legal/declaration-de-confidentialite"
                          class="font-semibold text-indigo-600"
                        >
                          {{ $t("common.newsletter-dialog.privacy-policy") }}</a
                        >.
                      </SwitchLabel>
                    </SwitchGroup>
                  </div>
                  <div class="mt-10">
                    <button
                      type="button"
                      :disabled="loading || !form.email || !tcAccepted"
                      class="block w-full rounded-md bg-gray-800 px-3.5 py-2.5 text-center text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                      @click="submit"
                    >
                      <template v-if="loading">
                        <svg
                          class="mr-3 h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          ></circle>
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {{ $t("common.processing") }}...
                      </template>
                      <span v-else>
                        {{ $t("common.newsletter-dialog.subscribe") }}
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
